/**
 * Encodes a saved view's filters to/from the flat URL query keys, using the
 * shared filter-key grammar (filterGrammar.ts).
 */

import type { FilterFieldAttribute, FilterType } from 'types/views';
import { getFilterDescriptor } from './filterDescriptors';
import {
  BOOKKEEPING_KEYS,
  EMPTY_OPERATOR,
  FILLED_OPERATOR,
  HANDLER_SUFFIX,
  isMinMaxKey,
  MAX_PREFIX,
  MIN_PREFIX,
  OPERATOR_SUFFIX,
  RANGE_END_SUFFIX,
  RANGE_START_SUFFIX,
  rangeFieldName,
  suffixFieldName,
  UNSET_BOUND,
} from './filterGrammar';

type AppliedFilter = {
  operator?: string;
  value?: unknown;
};

type ViewFilterField = {
  name: string;
  filter_type?: FilterType;
  applied_filter: AppliedFilter;
};

// Loose because the legacy TableView/TableFilter types omit `applied_filter`.
type ViewWithFilterFields = {
  filter_fields: any[];
};

const urlEntriesForFilter = (filter: ViewFilterField): [string, unknown][] => {
  const { operator, value } = filter.applied_filter;
  const { name, filter_type } = filter;

  if (operator === EMPTY_OPERATOR || operator === FILLED_OPERATOR) {
    return [[`${name}${HANDLER_SUFFIX}`, operator]];
  }

  const [lowerBound, upperBound] = Array.isArray(value) ? value : [];
  const operatorEntry: [string, unknown][] = operator
    ? [[`${name}${OPERATOR_SUFFIX}`, operator]]
    : [];

  if (filter_type === 'date') {
    return [
      [`${name}${RANGE_START_SUFFIX}`, lowerBound],
      [`${name}${RANGE_END_SUFFIX}`, upperBound],
      ...operatorEntry,
    ];
  }
  if (filter_type === 'number') {
    return [
      [`${MIN_PREFIX}${name}`, lowerBound],
      [`${MAX_PREFIX}${name}`, upperBound],
      ...operatorEntry,
    ];
  }
  return [[name, value], ...operatorEntry];
};

export const viewToUrlFilters = (view: ViewWithFilterFields): Record<string, unknown> =>
  Object.fromEntries(view.filter_fields.flatMap(urlEntriesForFilter));

// The grammar-stripped field a filter key belongs to, so a range (min_/max_ or
// _start/_end) and its empty/filled handler all collapse onto one filter.
const baseFieldName = (key: string): string => {
  const withoutPrefix = key.startsWith(MIN_PREFIX)
    ? key.slice(MIN_PREFIX.length)
    : key.startsWith(MAX_PREFIX)
      ? key.slice(MAX_PREFIX.length)
      : key;
  const suffix = [RANGE_START_SUFFIX, RANGE_END_SUFFIX, HANDLER_SUFFIX].find(token =>
    withoutPrefix.endsWith(token),
  );
  return suffix ? withoutPrefix.slice(0, -suffix.length) : withoutPrefix;
};

// The badge count: distinct active filters, so a date or number range counts once
// (not zero — which dropped date filters — and not twice).
export const countActiveFilters = (filters: Record<string, unknown>): number => {
  const activeFields = new Set<string>();
  for (const [key, value] of Object.entries(filters)) {
    if (!value || BOOKKEEPING_KEYS.includes(key) || key.includes(OPERATOR_SUFFIX)) continue;
    activeFields.add(baseFieldName(key));
  }
  return activeFields.size;
};

const isRangeOrOperatorKey = (key: string) =>
  key.includes(OPERATOR_SUFFIX) ||
  key.includes(HANDLER_SUFFIX) ||
  key.includes(RANGE_START_SUFFIX) ||
  key.includes(RANGE_END_SUFFIX) ||
  isMinMaxKey(key);

type FilterField = { name: string; value: unknown };

type FilterMeta = { name: string; filter_type?: FilterType; is_api?: boolean };

// A self-encoded multi-select (e.g. last_reminder_step_type) carries its own `[a,b]`
// URL encoding (see serializeFiltersToSearch), so it must not be re-wrapped here.
const isMultiSelect = (meta: FilterMeta | undefined): boolean =>
  !!meta &&
  !getFilterDescriptor(meta.name).selfEncoded &&
  (meta.filter_type === 'select' || meta.is_api === true);

/**
 * Turn the flat URL filters back into the nested `filter_fields` attributes the
 * views API expects, marking removed saved fields with `_destroy`.
 *
 * Multi-select filter values are normalised to arrays: a single selection
 * round-trips through the URL as a scalar (qs comma format drops the array
 * wrapper for one value), and the backend needs an array to iterate over.
 */
export const buildFilterFieldAttributes = (
  filters: Record<string, unknown>,
  currentFilterFields: Array<{ name: string; [key: string]: unknown }>,
  availableFilters: FilterMeta[] = [],
): FilterFieldAttribute[] => {
  const entries = Object.entries(filters);

  const multiSelectNames = new Set(
    availableFilters.filter(meta => isMultiSelect(meta)).map(meta => meta.name),
  );

  const normalizeMultiSelectValue = (name: string, value: unknown): unknown =>
    multiSelectNames.has(name) && value != null && value !== '' && !Array.isArray(value)
      ? [value]
      : value;

  const operators: Record<string, unknown> = {};
  entries.forEach(([key, value]) => {
    if (key.includes(OPERATOR_SUFFIX) || key.includes(HANDLER_SUFFIX)) {
      operators[suffixFieldName(key)] = value;
    }
  });

  const plainFields: FilterField[] = entries
    .filter(([key]) => !isRangeOrOperatorKey(key) && !BOOKKEEPING_KEYS.includes(key))
    .map(([name, value]) => ({ name, value }));

  // A standalone `_end` (no `_start`) emits nothing — the original self-cancelled.
  const minMaxFieldsAdded = new Set<string>();
  const combinedFields: FilterField[] = [];
  entries.forEach(([key]) => {
    if (key.includes(HANDLER_SUFFIX)) {
      combinedFields.push({ name: suffixFieldName(key), value: '' });
    } else if (key.includes(RANGE_START_SUFFIX)) {
      const fieldName = suffixFieldName(key);
      combinedFields.push({
        name: fieldName,
        value: [
          filters[`${fieldName}${RANGE_START_SUFFIX}`] || UNSET_BOUND,
          filters[`${fieldName}${RANGE_END_SUFFIX}`] || UNSET_BOUND,
        ],
      });
    } else if (!key.includes(OPERATOR_SUFFIX) && isMinMaxKey(key)) {
      const fieldName = rangeFieldName(key);
      if (minMaxFieldsAdded.has(fieldName)) return;
      minMaxFieldsAdded.add(fieldName);
      combinedFields.push({
        name: fieldName,
        value: [
          filters[`${MIN_PREFIX}${fieldName}`] || UNSET_BOUND,
          filters[`${MAX_PREFIX}${fieldName}`] || UNSET_BOUND,
        ],
      });
    }
  });

  const filterFieldAttributes = [...plainFields, ...combinedFields].map(({ name, value }) => ({
    name,
    applied_filter: { operator: operators[name], value: normalizeMultiSelectValue(name, value) },
  }));

  const destroyedFields = currentFilterFields
    .filter(field => !filterFieldAttributes.some(({ name }) => name === field.name))
    .map(field => ({ ...field, _destroy: true }));

  return [...filterFieldAttributes, ...destroyedFields];
};

const WIDE_COLUMNS = ['status', 'reference', 'full_name', 'debtors__full_name', 'recovery_step_id'];
const MEDIUM_COLUMNS = ['total_tvac', 'remaining_balance'];

export const columnWidth = (columnName: string): number | undefined => {
  if (WIDE_COLUMNS.includes(columnName)) return 400;
  if (MEDIUM_COLUMNS.includes(columnName)) return 100;
  return undefined;
};
